import SoftBox from "components/SoftComponents/SoftBox";
import { useQuery } from '@tanstack/react-query';
import { getCameraFeed, loadToken } from 'services/api/bks';
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { useEffect, useState } from "react";
import { loadingScreen } from "utils/wrapper";
import { useSoftUIController } from "hooks/context";

function CameraLatest({ uuid, refetch = false, zoom = false, is_occupancy = false, line_coordinates = false, no_padding = false }) {
  loadToken(useAuth0());

  const [controller] = useSoftUIController();
  const { deviceLayout, selectedOrg } = controller;

  const { isLoading, error, data, isFetching } = useQuery({
    queryKey: ['getCameraFeed', uuid, selectedOrg],
    queryFn: async () => {
      const data = await getCameraFeed(uuid);
      return data;
    },
    refetchInterval: refetch,
    staleTime: 60 * 1000
  });

  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (data) {
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      setImageUrl(url);

      return () => URL.revokeObjectURL(url);
    }
  }, [data]);

  if (isLoading || !imageUrl) {
    return loadingScreen(controller);
  }

  if (error) {
    return "Error! " + error.message;
  }

  if (!is_occupancy) {
    return (
      <PhotoProvider id={imageUrl}>
        <PhotoView src={imageUrl}>
          <SoftBox
            component="img"
            src={imageUrl}
            borderRadius="lg"
            maxHeight={{ xxs: '400px', x5l: '600px' }}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              minHeight: '100px',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              cursor: 'pointer',
              // aspectRatio: "16 / 9",
              pb: no_padding ? 0 : 1
            }}
          />
        </PhotoView>
      </PhotoProvider>
    );
  }
  else {
    return (
      <PhotoProvider id={imageUrl}>
        {zoom ? (
          <SoftBox
            position="relative"
            display="flex"
            flexDirection="column" // Stack children vertically
            justifyContent="center" // Centers vertically (along the main axis)
            alignItems="center"     // Centers horizontally (along the cross axis)
            width="100%"
            height="100%" // Uncomment this if the parent container has a fixed height
            minHeight="400px"  // Ensures there is enough vertical space to see the centering
            pb={1}
            flexShrink={1}
            sx={{ lineHeight: 0, overflow: 'hidden' }}
            borderRadius="12px"
          >
            <PhotoView src={imageUrl}>
              <SoftBox
                display="grid"
                sx={{
                  placeItems: 'center', // Keeps the img and svg aligned within the grid
                  width: '100%',
                  maxWidth: '100%',
                  aspectRatio: '640 / 480', // Maintains the image's original shape
                  position: 'relative',
                }}
              >
                <SoftBox
                  component="img"
                  src={imageUrl}
                  borderRadius="lg"
                  sx={{
                    gridArea: '1 / 1',
                    width: '100%',
                    height: '100%',
                    /** * 'contain' ensures the image shrinks to fit the height 
                     * or width without cutting off edges.
                     */
                    objectFit: 'contain',
                    cursor: 'pointer',
                  }}
                />
                {line_coordinates && (
                  <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    style={{
                      gridArea: '1 / 1',
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  >
                    <defs>
                      <pattern
                        id="hazardStripes"
                        patternUnits="userSpaceOnUse"
                        width="4"
                        height="4"
                        patternTransform="rotate(45)"
                      >
                        <rect width="4" height="4" fill="#000000" />
                        <rect width="2" height="4" fill="#FFD700" />
                      </pattern>
                    </defs>
                    <line
                      x1={`${(line_coordinates.x1 / 640) * 100}%`}
                      y1={`${(line_coordinates.y1 / 480) * 100}%`}
                      x2={`${(line_coordinates.x2 / 640) * 100}%`}
                      y2={`${(line_coordinates.y2 / 480) * 100}%`}
                      stroke="url(#hazardStripes)"
                      strokeWidth="1.5"
                      strokeLinecap="butt"
                      opacity={0.5}
                    />
                  </svg>
                )}
              </SoftBox>
            </PhotoView>
          </SoftBox>
        ) : (
          <SoftBox
            position="relative"
            display="flex"
            justifyContent="center"
            width="100%"
            pb={1}
            sx={{ lineHeight: 0 }}
          >
            <PhotoView src={imageUrl}>
              <SoftBox
                display="grid"
                sx={{
                  placeItems: 'center',
                  width: 'fit-content',
                  aspectRatio: '640 / 480',
                  position: 'relative',
                  lineHeight: 0
                }}
              >
                <SoftBox
                  component="img"
                  src={imageUrl}
                  borderRadius="lg"
                  maxHeight={{ xxs: '400px', x5l: '600px' }}
                  sx={{
                    gridArea: '1 / 1',
                    maxWidth: '100%',
                    height: 'auto',
                    minHeight: '300px',
                    objectFit: 'contain',
                    cursor: 'pointer',
                  }}
                />
                {line_coordinates && (
                  <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    style={{
                      gridArea: '1 / 1',
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none'
                    }}
                  >
                    <defs>
                      <pattern
                        id="hazardStripes"
                        patternUnits="userSpaceOnUse" // Keeps stripes consistent regardless of line length
                        width="4" // Total width of one black + one yellow stripe
                        height="4"
                        patternTransform="rotate(45)" // Standard hazard angle
                      >
                        <rect width="4" height="4" fill="#000000" />
                        <rect width="2" height="4" fill="#FFD700" />
                      </pattern>
                    </defs>
                    <line
                      x1={`${(line_coordinates.x1 / 640) * 100}%`}
                      y1={`${(line_coordinates.y1 / 480) * 100}%`}
                      x2={`${(line_coordinates.x2 / 640) * 100}%`}
                      y2={`${(line_coordinates.y2 / 480) * 100}%`}
                      stroke="url(#hazardStripes)"
                      strokeWidth="1.5"
                      strokeLinecap="butt"
                      opacity={0.5}
                    />
                  </svg>
                )}
              </SoftBox>
            </PhotoView>
          </SoftBox>
        )}
      </PhotoProvider>
    );
  }
}

// Typechecking props for the CameraLatest
CameraLatest.propTypes = {
  uuid: PropTypes.string,
  refetch: PropTypes.oneOf([PropTypes.number, PropTypes.bool]),
  zoom: PropTypes.bool,
  is_occupancy: PropTypes.bool,
  line_coordinates: PropTypes.oneOf([PropTypes.object, PropTypes.bool]),
  no_padding: PropTypes.bool,
};

export default CameraLatest;
